/**
 * Utilities for managing React Flow handle IDs with block ID prefixing
 *
 * This module provides functions to create globally unique handle IDs
 * by prefixing them with block IDs, and to parse them back to get
 * the original port names for pipeline configuration.
 */

/**
 * Create a globally unique handle ID by prefixing with block ID
 *
 * @param blockId - The block ID (e.g., "transform_abc123")
 * @param portName - The port name (e.g., "data", "result", "transformed_data")
 * @returns Prefixed handle ID (e.g., "transform_abc123.data")
 */
export function createHandleId(blockId: string, portName: string): string {
  return `${blockId}.${portName}`;
}

/**
 * Parse a prefixed handle ID back to block ID and port name
 *
 * @param handleId - The prefixed handle ID (e.g., "transform_abc123.data")
 * @returns Object with blockId and portName
 */
export function parseHandleId(handleId: string): {
  blockId: string;
  portName: string;
} {
  const lastPeriod = handleId.lastIndexOf('.');

  if (lastPeriod === -1) {
    // Fallback for non-prefixed handle IDs (backward compatibility)
    return {
      blockId: '',
      portName: handleId,
    };
  }

  return {
    blockId: handleId.substring(0, lastPeriod),
    portName: handleId.substring(lastPeriod + 1),
  };
}

/**
 * Check if a handle ID is prefixed (contains block ID)
 *
 * @param handleId - The handle ID to check
 * @returns True if the handle ID is prefixed
 */
export function isHandleIdPrefixed(handleId: string): boolean {
  return handleId.indexOf('.') !== -1;
}

/**
 * Extract just the port name from a handle ID (strips block ID prefix if present)
 *
 * @param handleId - The handle ID (prefixed or unprefixed)
 * @returns The port name only
 */
export function getPortNameFromHandleId(handleId: string): string {
  return parseHandleId(handleId).portName;
}

/**
 * Extract just the block ID from a handle ID (if prefixed)
 *
 * @param handleId - The handle ID (prefixed or unprefixed)
 * @returns The block ID or empty string if not prefixed
 */
export function getBlockIdFromHandleId(handleId: string): string {
  return parseHandleId(handleId).blockId;
}
